import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { assignItem } from '../api/items';
import useItemSearch from '../hooks/useItemSearch';
import SearchPagination from '../components/SearchPagination';
import { useToast } from '../contexts/ToastContext';
import { useSlideOverStore } from '../store/slideOverStore';
import SlideOverPanel from '../components/SlideOverPanel';
import ItemDetailPage from './ItemDetailPage';
import { truncateSummary } from '../utils/text';
import {
  ITEM_STATUSES as statusOptions,
  getStatusOption,
  getCategoryLabel,
  getCategoryColor,
  DEFAULT_PAGE_SIZE,
  DEFAULT_STALE_TIME,
} from '../constants';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  FunnelIcon,
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// Custom bullseye icon for relevance score with score-based coloring
// Red (< 2.5): outer ring colored, inner ring and dot gray
// Yellow (2.5-3.4): inner ring colored, outer ring and dot gray
// Green (>= 3.5): inner circle filled with green (gray stroke), outer ring gray
const ScoreBullseyeIcon = ({ score, className = 'h-4 w-4' }) => {
  const gray = '#9ca3af'; // gray-400
  const red = '#ef4444';  // red-500
  const yellow = '#eab308'; // yellow-500
  const green = '#22c55e'; // green-500

  let outerColor = gray;
  let innerColor = gray;
  let innerFill = 'none';
  let dotColor = gray;

  if (score < 2.5) {
    outerColor = red;
  } else if (score < 3.5) {
    innerColor = yellow;
  } else {
    innerFill = green;
    dotColor = '#ffffff'; // white dot for definition
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" stroke={outerColor} />
      <circle cx="12" cy="12" r="6" stroke={innerColor} fill={innerFill} />
      <circle cx="12" cy="12" r="2" fill={dotColor} stroke="none" />
    </svg>
  );
};

// Quality score icon component - circle stays gray, inner symbol is colored
// Green check (>= 3.5), yellow exclamation (2.5-3.4), red X (< 2.5)
const QualityScoreIcon = ({ score, className = 'h-4 w-4' }) => {
  const gray = '#9ca3af'; // gray-400
  const red = '#ef4444';  // red-500
  const yellow = '#eab308'; // yellow-500
  const green = '#22c55e'; // green-500

  let symbolColor;
  let symbol;

  if (score >= 3.5) {
    symbolColor = green;
    // Check mark path
    symbol = <path strokeLinecap="round" strokeLinejoin="round" d="M8 13L11 16 16 9" stroke={symbolColor} />;
  } else if (score >= 2.5) {
    symbolColor = yellow;
    // Exclamation mark (line + dot)
    symbol = (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75" stroke={symbolColor} />
        <circle cx="12" cy="16" r="0.75" fill={symbolColor} stroke="none" />
      </>
    );
  } else {
    symbolColor = red;
    // X mark path
    symbol = <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 7.5l9 9m0-9l-9 9" stroke={symbolColor} />;
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" stroke={gray} />
      {symbol}
    </svg>
  );
};

function ItemCard({
  item,
  onDelete,
  onStatusChange,
  onAddToIssue,
  onReprocess,
  isReprocessing,
  onViewDetails,
}) {
  const statusOption = getStatusOption(item.status);

  const handleStatusChange = (newStatus) => {
    onStatusChange(item.hash, newStatus);
  };

  const renderActionButtons = () => {
    if (item.status === 'process') {
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => onAddToIssue(item.hash)}
            className="px-3 py-1 bg-obn-blue text-white text-sm rounded-md hover:bg-obn-blue/90"
          >
            Add to Issue
          </button>
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Mark Ready
          </button>
          <button
            onClick={() => handleStatusChange('review')}
            className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700"
          >
            Review
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Block
          </button>
        </div>
      );
    } else if (item.status === 'review') {
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Mark Ready
          </button>
          <button
            onClick={() => onReprocess(item.hash)}
            disabled={isReprocessing}
            className={`px-3 py-1 text-white text-sm rounded-md ${
              isReprocessing
                ? 'bg-obn-blue/50 cursor-not-allowed'
                : 'bg-obn-blue hover:bg-obn-blue/90'
            }`}
          >
            {isReprocessing ? 'Reprocessing...' : 'Reprocess'}
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
          >
            Delete
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600"
          >
            Block
          </button>
        </div>
      );
    } else if (item.status === 'ready') {
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => onAddToIssue(item.hash)}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Add to Issue
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Block
          </button>
        </div>
      );
    } else if (item.status === 'assigned') {
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => onAddToIssue(item.hash)}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Assign to Another Issue
          </button>
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-obn-blue text-white text-sm rounded-md hover:bg-obn-blue/90"
          >
            Unassign
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Block
          </button>
        </div>
      );
    } else if (item.status === 'archived') {
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-obn-blue text-white text-sm rounded-md hover:bg-obn-blue/90"
          >
            Unarchive
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Block
          </button>
        </div>
      );
    } else if (item.status === 'blocked') {
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Unblock
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
        </div>
      );
    } else if (item.status === 'used') {
      // Previously used URL reappeared - quick reassign workflow
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => onAddToIssue(item.hash)}
            className="px-3 py-1 bg-obn-blue text-white text-sm rounded-md hover:bg-obn-blue/90"
          >
            Add to Current Issue
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Mark Ready
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
        </div>
      );
    } else if (item.status === 'error') {
      // Processing failed - retry workflow
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => handleStatusChange('process')}
            className="px-3 py-1 bg-obn-blue text-white text-sm rounded-md hover:bg-obn-blue/90"
          >
            Retry Processing
          </button>
          <button
            onClick={() => handleStatusChange('ready')}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            Mark Ready (Skip AI)
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
        </div>
      );
    } else if (item.status === 'duplicate') {
      // Duplicate content detected - review workflow
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => onAddToIssue(item.hash)}
            className="px-3 py-1 bg-obn-blue text-white text-sm rounded-md hover:bg-obn-blue/90"
          >
            Add to Issue Anyway
          </button>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Block
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
        </div>
      );
    } else if (item.status === 'missing-content') {
      // Needs content capture via browser extension
      return (
        <div className="flex flex-wrap gap-2 mt-3">
          <a
            href={`${item.url}#obn-ref`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Capture Content
          </a>
          <button
            onClick={() => handleStatusChange('archived')}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
          >
            Archive
          </button>
          <button
            onClick={() => handleStatusChange('blocked')}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Block
          </button>
          <button
            onClick={() => onDelete(item.hash)}
            className="px-3 py-1 bg-obn-red text-white text-sm rounded-md hover:bg-obn-red/90"
          >
            Delete
          </button>
        </div>
      );
    }
    return null;
  };

  // Helper to format date - handles both ISO strings and timestamps
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = typeof dateValue === 'number' ? new Date(dateValue) : new Date(dateValue);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  // Helper to get domain
  const domain = item.meta?.domain || item.domain;
  // Helper to get issues
  const issues = item.meta?.issues || item.issueNumbers || [];
  // Helper to get scores
  const qualityScore = item.ai?.qualityScore ?? item.qualityScore;
  const relevanceScore = item.ai?.relevanceScore ?? item.relevanceScore;
  // Helper to get category
  const canonicalCategory = item.category;
  const aiCategory = item.ai?.category || item.suggestedCategory;
  const displayCategory = canonicalCategory || aiCategory;
  const isAiSuggested = !canonicalCategory && aiCategory;
  const hasNoCategory = !displayCategory;
  // Helper to get published date
  const publishedDate = item.published ? new Date(item.published).toLocaleDateString() : null;

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:ring-1 dark:ring-white/10 p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
      onDoubleClick={() => onViewDetails(item.hash)}
    >
      {/* Title row */}
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-semibold text-obn-blue dark:text-white flex-1 mr-4">{item.title || 'Untitled'}</h3>
        <button
          onClick={() => onViewDetails(item.hash)}
          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-obn-blue/30 bg-obn-lightblue text-obn-blue hover:bg-obn-blue hover:text-white transition-colors"
          title="View full item details"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          Details
        </button>
      </div>

      {/* Status, Category, Domain, Hash, Issues row */}
      <div className="flex items-center flex-wrap gap-2 text-sm">
        {/* Status chip */}
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded border ${statusOption?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}
        >
          {statusOption?.label || item.status}
        </span>
        {/* Category chip */}
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded border ${
            hasNoCategory
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 italic'
              : isAiSuggested
                ? `${getCategoryColor(displayCategory)} italic`
                : getCategoryColor(displayCategory)
          }`}
          title={hasNoCategory ? 'No category assigned' : isAiSuggested ? 'AI suggested category' : 'Category'}
        >
          {hasNoCategory ? 'No category' : getCategoryLabel(displayCategory)}
        </span>
        {/* Domain */}
        {domain && <span className="text-gray-500 dark:text-gray-400">{domain}</span>}
        <span className="text-gray-400 dark:text-gray-500">|</span>
        {/* Hash */}
        <span className="text-xs font-mono text-gray-400 dark:text-gray-500">{item.hash}</span>
        {/* Issues */}
        {issues.length > 0 && (
          <>
            <span className="text-gray-400 dark:text-gray-500">|</span>
            <div className="flex items-center gap-1">
              {issues.map((issueNum) => (
                <a
                  key={issueNum}
                  href={`/issues/${issueNum}`}
                  className="px-1.5 py-0.5 text-xs font-mono bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 border border-blue-200 dark:border-blue-800/50 rounded hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                >
                  #{issueNum}
                </a>
              ))}
            </div>
          </>
        )}
      </div>

      {/* URL row */}
      <div className="mt-2 flex items-center gap-1">
        <DocumentTextIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-obn-blue dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 truncate"
        >
          {item.url}
        </a>
        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-obn-blue dark:text-blue-400 flex-shrink-0" />
      </div>

      {/* Meta row: Author, Date, Time, Scores */}
      <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
        {/* Author */}
        {item.authors && item.authors.length > 0 && (
          <div className="flex items-center gap-1">
            <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            <span>{item.authors.filter(Boolean).join(', ')}</span>
          </div>
        )}
        {/* Published date */}
        {publishedDate && (
          <div className="flex items-center gap-1">
            <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            <span>{publishedDate}</span>
          </div>
        )}
        {/* Time to read */}
        {item.timeToRead != null && (
          <div className="flex items-center gap-1">
            <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            <span>{item.timeToRead} min read</span>
          </div>
        )}
        {/* Score indicators */}
        {(qualityScore != null && qualityScore > 0) || (relevanceScore != null && relevanceScore > 0) ? (
          <div className="flex items-center gap-3">
            {qualityScore != null && qualityScore > 0 && (
              <div className="flex items-center gap-0.5" title={`Quality: ${qualityScore.toFixed(1)}/5`}>
                <QualityScoreIcon score={qualityScore} className="h-4 w-4" />
                {[1, 2, 3, 4, 5].map((bar) => {
                  const fillPercent = Math.max(0, Math.min(1, qualityScore - (bar - 1))) * 100;
                  const fillColor = qualityScore < 2.5 ? 'bg-red-500' : qualityScore < 3.5 ? 'bg-yellow-500' : 'bg-green-500';
                  return (
                    <div key={bar} className="w-3 h-2 rounded-sm bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      {fillPercent > 0 && (
                        <div className={`h-full ${fillColor}`} style={{ width: `${fillPercent}%` }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {relevanceScore != null && relevanceScore > 0 && (
              <div className="flex items-center gap-0.5" title={`Relevance: ${relevanceScore.toFixed(1)}/5`}>
                <ScoreBullseyeIcon score={relevanceScore} className="h-4 w-4" />
                {[1, 2, 3, 4, 5].map((bar) => {
                  const fillPercent = Math.max(0, Math.min(1, relevanceScore - (bar - 1))) * 100;
                  const fillColor = relevanceScore < 2.5 ? 'bg-red-500' : relevanceScore < 3.5 ? 'bg-yellow-500' : 'bg-green-500';
                  return (
                    <div key={bar} className="w-3 h-2 rounded-sm bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      {fillPercent > 0 && (
                        <div className={`h-full ${fillColor}`} style={{ width: `${fillPercent}%` }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Summary with horizontal rules */}
      {(item.ai?.summary || item.summary) && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-200">{truncateSummary(item.ai?.summary || item.summary, 750)}</p>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <div>Created: {formatDate(item.created || item._createdTimestamp)}</div>
        <div>Modified: {formatDate(item.modified || item._modifiedTimestamp)}</div>
      </div>

      {renderActionButtons()}
    </div>
  );
}

export default function ItemsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL state - filter is the filter type ('review' for all review, or 'status:{value}' for specific status)
  const filter = searchParams.get('filter') || 'review';
  const searchQuery = searchParams.get('q') || '';
  const sortOrder = searchParams.get('sort') || 'desc';

  // Local state
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [cursor, setCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);

  // Derived state
  const isSearchMode = searchQuery.length > 0;
  const reverseSort = sortOrder === 'desc';

  // Parse filter into filterBy and filterValue for API
  const getFilterParams = () => {
    if (filter === 'all') {
      return { filterBy: 'all', filterValue: '' };
    }
    if (filter === 'review') {
      return { filterBy: 'review', filterValue: '' };
    } else if (filter.startsWith('status:')) {
      return { filterBy: 'status', filterValue: filter.replace('status:', '') };
    }
    return { filterBy: 'review', filterValue: '' };
  };

  const { filterBy, filterValue } = getFilterParams();

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Slide-over panel state for item details
  const {
    isOpen: isSlideOverOpen,
    itemHash: slideOverItemHash,
    openSlideOver,
    closeSlideOver,
    navigatePrevious,
    navigateNext,
    getPosition,
  } = useSlideOverStore();

  // Search functionality - pass filter option (sort is disabled during search)
  const search = useItemSearch({ filter: filter !== 'all' ? filter : undefined });

  // Sync search input with URL on mount
  useEffect(() => {
    if (searchQuery && searchQuery !== search.searchQuery) {
      search.setSearchTerm(searchQuery);
      search.handleSearch(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - intentionally empty deps to prevent infinite loops

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  const {
    data: itemsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['items', filterBy, filterValue, cursor, reverseSort],
    queryFn: async () => {
      const params = { filterBy, limit: DEFAULT_PAGE_SIZE };
      // 'review' and 'all' filters don't need a filterValue
      if (filterBy !== 'review' && filterBy !== 'all') {
        params.filterValue = filterValue;
      }
      if (cursor) params.cursor = cursor;
      if (reverseSort) params.reverse = 'true';

      const response = await apiClient.get('/api/items', { params });
      return response.data;
    },
    // Only run filter query when not in search mode
    enabled: !isSearchMode && !!filterBy && (filterBy === 'review' || filterBy === 'all' || !!filterValue),
    staleTime: DEFAULT_STALE_TIME,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ hash, data }) => {
      const response = await apiClient.put(`/api/items/${hash}`, data);
      return response.data;
    },
    onMutate: async ({ hash, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['items'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ['items'] });

      // Optimistically update the cache
      queryClient.setQueriesData({ queryKey: ['items'] }, (oldData) => {
        if (!oldData || !oldData.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.map((item) => (item.hash === hash ? { ...item, ...data } : item)),
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      showError('Failed to update item. Please try again.');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (hash) => {
      await apiClient.delete(`/api/items/${hash}`);
    },
    onMutate: async (deletedHash) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['items'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ['items'] });

      // Optimistically remove the deleted item
      queryClient.setQueriesData({ queryKey: ['items'] }, (oldData) => {
        if (!oldData || !oldData.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.filter((item) => item.hash !== deletedHash),
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      showError('Failed to delete item. Please try again.');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const assignMutation = useMutation({
    mutationFn: async (itemHash) => {
      return await assignItem(itemHash);
    },
    onMutate: async (itemHash) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['items'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ['items'] });

      // Optimistically remove the item from the current filter view
      // Since adding to issue changes status to 'assigned', it should disappear from 'process' filter
      queryClient.setQueriesData({ queryKey: ['items'] }, (oldData) => {
        if (!oldData || !oldData.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.filter((item) => item.hash !== itemHash),
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      showError('Failed to add item to issue. Please try again.');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: async (itemHash) => {
      const response = await apiClient.post(`/api/items/${itemHash}/reprocess`);
      return response.data;
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to reprocess item. Please try again.');
    },
    onSuccess: (updatedItem) => {
      showSuccess('Successfully reprocessed item with AI analysis!');

      // Update the cache with the updated item
      queryClient.setQueriesData({ queryKey: ['items'] }, (oldData) => {
        if (!oldData || !oldData.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.map((item) => (item.hash === updatedItem.hash ? updatedItem : item)),
        };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const handleDelete = (hash) => {
    // Find the item to show in confirmation dialog
    const item = itemsData?.items?.find((i) => i.hash === hash) ||
                 search.searchResults?.items?.find((i) => i.hash === hash);
    if (item) {
      setItemToDelete(item);
    }
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete.hash);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setItemToDelete(null);
  };

  const handleStatusChange = (hash, newStatus) => {
    updateMutation.mutate({ hash, data: { status: newStatus } });
  };

  const handleAddToIssue = (itemHash) => {
    assignMutation.mutate(itemHash);
  };

  const handleReprocess = (itemHash) => {
    reprocessMutation.mutate(itemHash);
  };

  const handleViewDetails = (itemHash) => {
    // Get the list of item hashes for navigation based on current mode
    const itemList = isSearchMode
      ? search.searchResults?.items?.map((item) => item.hash) || []
      : itemsData?.items?.map((item) => item.hash) || [];
    openSlideOver(itemHash, `/items?${searchParams.toString()}`, itemList);
  };

  // Update URL params helper
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  const handleFilterSelect = (newFilter) => {
    updateParams({ filter: newFilter, q: null });
    setCursor(null);
    setCursorHistory([]);
    setShowFilterDropdown(false);
    // Clear search state
    setSearchInput('');
    search.handleClearSearch();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      updateParams({ q: searchInput.trim() });
      search.setSearchTerm(searchInput.trim());
      search.handleSearch(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    updateParams({ q: null });
    search.handleClearSearch();
  };

  const handleReverseSortToggle = () => {
    const newSort = reverseSort ? 'asc' : 'desc';
    updateParams({ sort: newSort });
    setCursor(null);
    setCursorHistory([]);
  };

  // Get current data and loading state based on mode
  const getCurrentData = () => {
    if (isSearchMode) {
      return {
        items: search.shouldShowResults ? search.searchResults.items : [],
        isLoading: search.isSearching,
        error: search.error,
        hasResults: search.shouldShowResults,
        isEmpty: search.shouldShowEmptyState,
        totalHits: search.searchResults.totalHits || 0,
      };
    } else {
      return {
        items: itemsData?.items || [],
        isLoading,
        error,
        hasResults: itemsData && itemsData.items && itemsData.items.length > 0,
        isEmpty: itemsData && (!itemsData.items || itemsData.items.length === 0),
        totalHits: null,
      };
    }
  };

  const currentData = getCurrentData();

  // Get display label for current filter
  const getFilterLabel = () => {
    if (filter === 'all') return 'All Items';
    if (filter === 'review') return 'All Review Items';
    if (filter.startsWith('status:')) {
      const statusValue = filter.replace('status:', '');
      const statusOpt = statusOptions.find((s) => s.value === statusValue);
      return statusOpt?.label || statusValue;
    }
    return 'All Review Items';
  };

  const handleNextPage = () => {
    if (itemsData?.pagination?.hasMore && itemsData.pagination.cursor) {
      // Add current cursor to history before moving to next page
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(itemsData.pagination.cursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      // Go back to the previous cursor
      const previousCursor = cursorHistory[cursorHistory.length - 1];
      const newHistory = cursorHistory.slice(0, -1); // Remove last cursor from history

      setCursor(previousCursor);
      setCursorHistory(newHistory);
    }
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-obn-blue dark:text-white">Items</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Manage content items from your feeds</p>
        </div>
      </div>

      {/* Unified Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:ring-1 dark:ring-white/10 border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleClearSearch();
                  }
                }}
                placeholder="Search items..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </form>

          {/* Filter Dropdown */}
          <div className="relative" ref={filterDropdownRef}>
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-2 px-4 py-2 border rounded-md transition-colors min-w-[180px] justify-between bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-4 w-4" />
                <span className="text-sm font-medium">{getFilterLabel()}</span>
              </div>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showFilterDropdown && (
              <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg dark:ring-1 dark:ring-white/10 z-20 max-h-80 overflow-y-auto">
                {/* All Items - for search without filter */}
                <button
                  onClick={() => handleFilterSelect('all')}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    filter === 'all' ? 'bg-obn-blue/10 dark:bg-blue-900/40 text-obn-blue dark:text-blue-200 font-medium' : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  All Items
                </button>

                {/* All Review Items */}
                <button
                  onClick={() => handleFilterSelect('review')}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    filter === 'review' ? 'bg-obn-blue/10 dark:bg-blue-900/40 text-obn-blue dark:text-blue-200 font-medium' : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  All Review Items
                </button>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

                {/* By Status Section */}
                <div className="px-4 py-1">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">By Status</span>
                </div>
                {statusOptions.map((status) => (
                  <button
                    key={status.value}
                    onClick={() => handleFilterSelect(`status:${status.value}`)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      filter === `status:${status.value}` ? 'bg-obn-blue/10 dark:bg-blue-900/40 text-obn-blue dark:text-blue-200 font-medium' : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sort Button - disabled during search */}
          <button
            onClick={handleReverseSortToggle}
            disabled={isSearchMode}
            className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isSearchMode
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : reverseSort
                  ? 'bg-obn-blue text-white hover:bg-obn-blue/90'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            title={isSearchMode ? 'Sort not available during search' : reverseSort ? 'Sort oldest first' : 'Sort newest first'}
          >
            {reverseSort ? (
              <>
                <ArrowUpIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Newest</span>
              </>
            ) : (
              <>
                <ArrowDownIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Oldest</span>
              </>
            )}
          </button>
        </div>

        {/* Active search indicator */}
        {isSearchMode && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 flex-wrap">
            <span>Searching for:</span>
            <span className="font-medium text-obn-blue dark:text-blue-400">&quot;{searchQuery}&quot;</span>
            {filter !== 'review' && (
              <span className="text-gray-500 dark:text-gray-400">in {getFilterLabel()}</span>
            )}
            {search.searchResults?.totalHits > 0 && (
              <span className="text-gray-500 dark:text-gray-400">({search.searchResults.totalHits} results)</span>
            )}
            <button
              onClick={handleClearSearch}
              className="ml-auto text-sm text-obn-blue dark:text-blue-400 hover:underline hover:text-obn-blue dark:hover:text-blue-300"
            >
              Clear search
            </button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {currentData.isLoading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-obn-blue"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {isSearchMode ? 'Searching...' : 'Loading items...'}
          </p>
        </div>
      )}

      {/* Error State */}
      {currentData.error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-md p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">
            Error {isSearchMode ? 'searching' : 'loading'} items: {currentData.error.message}
          </p>
        </div>
      )}

      {/* Items List */}
      {currentData.hasResults && (
        <div className="space-y-6">
          {currentData.items.map((item) => {
            // For search results, item might have different structure (Algolia hit)
            const itemData = isSearchMode
              ? {
                    // Convert Algolia hit back to item format
                    ...item,
                    // Handle instances data from Algolia record
                    title: item.instances
                      ? Object.values(item.instances)[0]?.title || item.title
                      : item.title,
                    status: item.instances
                      ? Object.values(item.instances)[0]?.status || item.status
                      : item.status,
                    // Generate an ID for search results if needed
                    id: item.instances
                      ? `${item.hash}:${Object.keys(item.instances)[0]}`
                      : item.id || item.objectID,
                  }
                : item;

            // Use stable key based on mode - hash for filter mode, objectID for search mode
            const stableKey = isSearchMode ? item.objectID : item.hash;

            return (
              <ItemCard
                key={stableKey}
                item={itemData}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
                onAddToIssue={handleAddToIssue}
                onReprocess={handleReprocess}
                isReprocessing={reprocessMutation.isPending}
                onViewDetails={handleViewDetails}
              />
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {isSearchMode
        ? search.shouldShowResults &&
          search.searchResults.totalPages > 1 && (
            <SearchPagination
              searchResults={search.searchResults}
              onPageChange={search.handlePageChange}
              onNextPage={search.handleNextPage}
              onPrevPage={search.handlePrevPage}
              className="mt-8"
            />
          )
        : itemsData &&
          itemsData.items &&
          itemsData.items.length > 0 && (
            <div className="mt-8 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="text-sm text-gray-700 dark:text-gray-200">
                Showing {itemsData.items.length} items on page {cursorHistory.length + 1}
                {itemsData.pagination?.hasMore && ' (more available)'}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handlePrevPage}
                  disabled={cursorHistory.length === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    cursorHistory.length > 0
                      ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>

                <button
                  onClick={handleNextPage}
                  disabled={!itemsData.pagination?.hasMore}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    itemsData.pagination?.hasMore
                      ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}

      {/* Empty State */}
      {currentData.isEmpty && (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-obn-blue dark:text-white mb-2">
            {isSearchMode ? 'No search results' : 'No items found'}
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            {isSearchMode
              ? search.hasQuery
                ? `No items found for "${search.searchQuery}". Try different search terms.`
                : 'Enter search terms, then press Enter or click Search to find items.'
              : 'No items match the current filter criteria. Try adjusting your filters or check back later.'}
          </p>
        </div>
      )}

      {/* Mutation Loading States - toast notifications handle success/error */}
      {(updateMutation.isPending ||
        deleteMutation.isPending ||
        assignMutation.isPending ||
        reprocessMutation.isPending) && (
        <div className="fixed bottom-4 right-4 bg-obn-blue text-white px-4 py-2 rounded-lg shadow-lg z-40">
          {updateMutation.isPending
            ? 'Updating...'
            : deleteMutation.isPending
              ? 'Deleting...'
              : assignMutation.isPending
                ? 'Assigning to issue...'
                : 'Running AI analysis...'}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:ring-1 dark:ring-white/10 max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900/40 rounded-full">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete this item permanently?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  This will permanently delete this item and remove it from all issues it has been assigned to. This action cannot be undone.
                </p>
                <p className="mt-3 text-sm text-red-700 dark:text-red-300 font-medium break-words">
                  &quot;{itemToDelete.title || 'Untitled'}&quot;
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Details Slide-over Panel */}
      {slideOverItemHash && (
        <SlideOverPanel
          isOpen={isSlideOverOpen}
          onClose={closeSlideOver}
          title="Item Details"
          onNavigatePrevious={navigatePrevious}
          onNavigateNext={navigateNext}
          position={getPosition()}
        >
          <ItemDetailPage />
        </SlideOverPanel>
      )}
    </div>
  );
}
